import { useEffect, useMemo, useState } from "react";
import { ampli } from "../../../../ampli";
import {
  LandingPageOptions,
  PrimitiveValueType,
  RowData,
  RowDataValueType,
} from "../../../../types";
import { filterRecords } from "../../helpers";
import { CHIP_FILTER_FIELD_BY_SETUP } from "../../constants";

interface UseTableSearchProps {
  searchValue: string;
  setupName: string;
  chipFilterValue: PrimitiveValueType[];
  rowDataList: RowData[];
  gridFields: Set<string>;
  getFieldDisplayValue: (
    fieldName: string,
    rowData: RowData,
  ) => RowDataValueType;
  headerLabel: string;
  selected: LandingPageOptions;
  selectedDashboardTabSectionKey: string | undefined;
  setSearchValue: (value: string) => void;
  setTableTitleInfo: (title: string) => void;
  setTableTitleInfoForTab: (title: string) => void;
}

interface UseTableSearchReturn {
  dataToDisplay: RowData[];
  handleSearchInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchInputClear: () => void;
}

export function useTableSearch({
  searchValue,
  chipFilterValue,
  setupName,
  rowDataList,
  gridFields,
  getFieldDisplayValue,
  setSearchValue,
}: UseTableSearchProps): UseTableSearchReturn {
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchInputClear = () => setSearchValue("");

  // Debounce search input
  useEffect(() => {
    const debounced = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      if (searchValue) {
        ampli.performSearch({ "Search Bar": true, "Entry Name": searchValue });
      }
    }, 300);
    return () => clearTimeout(debounced);
  }, [searchValue]);

  // Filter data on debounced search change
  const dataToDisplay = useMemo(() => {
    const updatedTableData =
      debouncedSearchValue !== ""
        ? filterRecords(
            debouncedSearchValue,
            rowDataList,
            gridFields,
            getFieldDisplayValue,
          )
        : rowDataList;
    const chipFilterField = CHIP_FILTER_FIELD_BY_SETUP.get(setupName);

    if (!chipFilterField || chipFilterValue.length === 0) {
      return updatedTableData;
    }

    return updatedTableData.filter((record) =>
      chipFilterValue.includes(record[chipFilterField] as PrimitiveValueType),
    );
  }, [debouncedSearchValue, rowDataList, setupName, chipFilterValue]);

  return {
    dataToDisplay,
    handleSearchInputChange,
    handleSearchInputClear,
  };
}
